import baseApi from "./RootBaseApi";
import {
  Testimonial,
  TestimonialListQueryParams,
  TestimonialListResponse,
  TestimonialStatus,
  TestimonialTranslationPayload,
  TestimonialTranslations,
} from "@/types/testimonial.types";

export const TestimonialService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTestimonials: builder.query<
      TestimonialListResponse,
      TestimonialListQueryParams | void
    >({
      query: (params) => ({
        url: "/testimonials",
        method: "GET",
        params,
      }),
      providesTags: ["Testimonials"],
      keepUnusedDataFor: 0,
    }),
    getTestimonialById: builder.query<
      { status: boolean; message: string; data: Testimonial },
      string
    >({
      query: (id) => ({
        url: `/testimonials/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Testimonials", id }],
      keepUnusedDataFor: 0,
    }),
    createTestimonial: builder.mutation<Testimonial, FormData>({
      query: (body) => ({
        url: "/testimonials",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [{ type: "Testimonials", id: "LIST" }],
    }),
    updateTestimonial: builder.mutation<
      Testimonial,
      { id: string; body: FormData }
    >({
      query: ({ id, body }) => ({
        url: `/testimonials/${id}`,
        method: "PATCH",
        data: body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Testimonials", id },
        { type: "Testimonials", id: "LIST" },
      ],
    }),
    deleteTestimonial: builder.mutation<void, string>({
      query: (id) => ({
        url: `/testimonials/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Testimonials", id: "LIST" }],
    }),
    translateTestimonialContent: builder.mutation<
      TestimonialTranslations,
      TestimonialTranslationPayload
    >({
      query: (data) => ({
        url: "/testimonials/translate",
        method: "POST",
        data,
      }),
      transformResponse: (response: TestimonialTranslations) => response,
    }),
    // Public-facing hook can reuse getTestimonials with a "published" filter
    getPublishedTestimonials: builder.query<
      TestimonialListResponse,
      { limit?: number }
    >({
      query: ({ limit } = {}) => ({
        url: "/testimonials",
        method: "GET",
        params: {
          status: TestimonialStatus.PUBLISHED,
          limit,
        },
      }),
      providesTags: ["Testimonials"],
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetTestimonialsQuery,
  useGetTestimonialByIdQuery,
  useCreateTestimonialMutation,
  useUpdateTestimonialMutation,
  useDeleteTestimonialMutation,
  useGetPublishedTestimonialsQuery,
  useTranslateTestimonialContentMutation,
} = TestimonialService;
